import React, { useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FormControl, makeStyles, TextField, Button, FormControlLabel, Checkbox } from "@material-ui/core"
import { useFormik } from "formik"
import * as yup from "yup"
import { useTranslation } from "react-i18next"
import { DatePicker } from "../common/DatePicker"
import Color, { getColors } from "./Colors"
import { createEventRequest, createEventRequestReset } from "../../actions/eventActions"
import { PAYLOAD_DATE_FORMAT } from "../../utils"
import { useTheme } from "@material-ui/core/styles"
import moment from "moment"

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
		"& fieldset legend": {
			float: "none",
		},
		"& textarea": {
			boxShadow: "none",
		},
	},
	margin: {
		margin: theme.spacing(2),
	},
	datePickerMargin: {
		marginLeft: theme.spacing(2),
		marginBottom: theme.spacing(1),
	},
	dividerLine: {
		width: "100%",
		border: `1px solid ${theme.palette.grey[400]}`,
	},
	formActions: {
		display: "flex",
		justifyContent: "flex-end",
		flexDirection: "row",
		width: "100%",
	},
	formActionsBtn: {
		margin: "0.5rem",
		"&:disabled": {
			backgroundColor: "#e0e0e0 !important",
		},
	},
	addBtn: {
		textTransform: "initial",
		fontSize: "14px",
		fontWeight: 500,
		backgroundColor: theme.palette.secondary.dark,
	},
	colorLable: {
		fontSize: 14,
		fontWeight: 500,
		marginBottom: theme.spacing(1),
	},
	datePicker: {
		maxWidth: 180,
	},
}))

const CreateEvent = ({ callback }) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const [t] = useTranslation("global")
	const theme = useTheme()
	const colors = getColors(theme)
	const eventNameRef = useRef(null)

	const isLoading = useSelector((state) => state.events.isLoading)
	const isSaved = useSelector((state) => state.events.isSaved)
	const event = useSelector((state) => state.events.event)

	//form validation
	const validationSchema = yup.object({
		name: yup.string().trim().max(25).required(t("event.field.nameRequired")),
		description: yup.string().max(150, t("event.field.descriptionError")),
		start_date: yup.date().required(t("event.field.startDateRequired")),
		// .test('start-date', 'Start date should not be greater than end date', function(start_date) {
		// 	const { end_date } = this.parent;
		// 	return !end_date || !start_date || start_date <= end_date;
		//   }),
		end_date: yup.date().required(t("event.field.endDateRequired")),
		// .test("end-date", "End date should not be less than start date", function (end_date) {
		// 	const { start_date } = this.parent
		// 	return !start_date || !end_date || end_date >= start_date
		// }),
		colour: yup.string(),
	})

	const formik = useFormik({
		initialValues: {
			name: event?.name ? event?.name : "",
			description: event?.description ? event?.description : "",
			start_date: event?.start_date ? new Date(event?.start_date).toISOString() : new Date(),
			end_date: event?.end_date ? new Date(event?.end_date).toISOString() : new Date(),
			colour: event?.colour ? event?.colour : colors[0],
			multidate: event?.start_date && event?.end_date && event.start_date !== event.end_date,
		},
		validationSchema: validationSchema,
		onSubmit: (values) => {
			const payload = {
				...values,
				start_date: moment(values.start_date).format(PAYLOAD_DATE_FORMAT),
				end_date: moment(values.end_date).format(PAYLOAD_DATE_FORMAT),
			}

			if (event.id) {
				payload["id"] = event.id
			}

			dispatch(createEventRequest(payload))
		},
	})

	useEffect(() => {
		if (isSaved) {
			formik.resetForm()
			const reLoadList = true
			callback(reLoadList)
			dispatch(createEventRequestReset())
		}

		if (eventNameRef.current) {
			eventNameRef.current.focus()
		}
	}, [isSaved])

	return (
		<>
			<form className={classes.root} noValidate autoComplete="off" id="create-event" onSubmit={formik.handleSubmit}>
				<FormControl fullWidth className={classes.margin}>
					<TextField
						id="name"
						label={`${t("event.field.name")}*`}
						variant="outlined"
						value={formik.values.name}
						onChange={formik.handleChange}
						error={formik.touched.name && Boolean(formik.errors.name)}
						helperText={formik.touched.name && formik.errors.name}
						inputRef={eventNameRef}
					/>
				</FormControl>

				<FormControl fullWidth className={`${classes.margin} ${classes.formControl}`}>
					<TextField
						id="description"
						label={t("event.field.description")}
						multiline
						rows={4}
						variant="outlined"
						value={formik.values.description}
						onChange={formik.handleChange}
						error={formik.touched.description && Boolean(formik.errors.description)}
						helperText={formik.touched.description && formik.errors.description}
					/>
				</FormControl>

				<FormControl className={`${classes.margin} ${classes.formControl}`}>
					<FormControlLabel
						control={
							<Checkbox
								checked={formik.values.multidate}
								onChange={(e) => {
									formik.setFieldValue("multidate", e.target.checked)
									if (!e.target.checked) {
										formik.setFieldValue("end_date", formik.values.start_date)
									}
								}}
								name="multidate"
							/>
						}
						label="Multi Day Event"
					/>
				</FormControl>

				<FormControl fullWidth className={`${classes.datePickerMargin} ${classes.formControl}`}>
					<DatePicker
						label={`${t("event.field.startDate")}*`}
						date={formik.values.start_date}
						setDate={(e) => {
							const date = e?._d
							if (!date) return
							formik.setFieldValue("start_date", date)
							if (!formik.values.multidate) formik.setFieldValue("end_date", date)
						}}
						className={`${classes.datePicker}`}
						//minDate={minDate}
					/>
				</FormControl>

				<FormControl fullWidth className={`${classes.datePickerMargin} ${classes.formControl}`}>
					<DatePicker
						label={t("event.field.endDate")}
						date={formik.values.end_date}
						setDate={(e) => {
							const date = e?._d
							if (!date) return
							formik.setFieldValue("end_date", date)
						}}
						className={`${classes.datePicker}`}
						//format={PAYLOAD_DATE_FORMAT}
						//minDate={minDate}
						disabled={!formik.values.multidate}
					/>
				</FormControl>

				<FormControl fullWidth className={`${classes.margin} ${classes.formControl}`}>
					<label className={classes.colorLable}>{`${t("event.field.color")}*`}:</label>
					<Color selectedColor={formik.values.colour} setSelectedColor={(color) => formik.setFieldValue("colour", color)} />
				</FormControl>

				<hr className={classes.dividerLine} />
				<FormControl className={classes.formActions}>
					<Button
						variant="contained"
						color="primary"
						className={`${classes.formActionsBtn} ${classes.addBtn}`}
						//disabled={hasOCR ? formik.values.ocr_primary_language === "" : false}
						type="submit"
						disabled={isLoading}
					>
						{isLoading ? t("event.field.saving") : event?.id ? t("event.field.updateEvent") : t("event.field.addEvent")}
					</Button>
					<Button
						//variant="contained"
						className={classes.formActionsBtn}
						style={{
							textTransform: "initial",
							fontSize: "14px",
							padding: "8px 12px",
						}}
						onClick={() => callback(false)}
					>
						{t("event.field.cancel")}
					</Button>
				</FormControl>
			</form>
		</>
	)
}

export default CreateEvent
